import axios, {AxiosError, AxiosRequestConfig, AxiosResponse} from 'axios';
import {User} from 'firebase/auth';
import {BackendLanguageTag} from '../backend/enums/backend-language-tag';
import {AppConfig} from '../interfaces/app-config';
import {getBackendLanguageFromBrowserLanguage} from '../utils/functions/get-backend-language-from-browser-language';
import {Logger} from '../utils/managers/logger';
import {EventEmitter} from 'events';

// Define global event emitter
export const BackendApiManagerEventEmitter = new EventEmitter();
export enum BackendApiManagerEvents {
  QUICK_ACCESS_UNAUTHORIZED = 'QUICK_ACCESS_UNAUTHORIZED',
}

export interface BackendApiRequestConfig extends AxiosRequestConfig {
  /** Disable automatic replay for mutations without an idempotency contract. */
  retryOnBadGateway?: boolean;
}

export class BackendApiManager {
  // Singleton Instance
  private static _instance: BackendApiManager;

  // Private
  private logger: Logger;
  private user: User | undefined;
  private legacyAuthToken: string | undefined;
  private quickAccessToken: string | undefined;
  private baseUrl: string | undefined;

  static get instance(): BackendApiManager {
    if (!this._instance) {
      this._instance = new BackendApiManager();
    }
    return this._instance;
  }

  constructor() {
    // Create Logger
    this.logger = new Logger('BackendApiManager');

    // Init
    this.logger.info('Loaded');

    // Add interceptor
    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config;

        // Retry only for 502 status code
        if (
          error.response?.status === 502 &&
          (originalRequest as BackendApiRequestConfig | undefined)?.retryOnBadGateway !== false
        ) {
          try {
            // You can add delay before retrying the request if needed
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Warn
            this.logger.warn('Retrying request...');

            // Clone the original request and retry it
            const retryConfig = {...originalRequest};
            return axios.request(retryConfig);
          } catch (error) {
            // If the retry also fails, you can handle the error accordingly
            throw new Error('Retry failed');
          }
        }

        // Look only for 401 status code
        if (error.response?.status === 401 && this.quickAccessToken) {
          // Quick Access Token is not valid anymore, force logout
          BackendApiManagerEventEmitter.emit(BackendApiManagerEvents.QUICK_ACCESS_UNAUTHORIZED);

          // Pretend that the request was successful
          return Promise.resolve({message: 'Quick Access Unauthorized', status: 401});
        }

        // For other error codes or network issues, throw the error
        throw error;
      }
    );
  }

  private async getHeaders() {
    const result: any = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      // Fallback language to be used on the backend if no language is set for the user
      'Accept-Language': getBackendLanguageFromBrowserLanguage() || BackendLanguageTag.ptPT,
    };

    // Check if auth is available
    if (this.user) {
      // Append Header
      result.Authorization = `Bearer ${await this.user.getIdToken()}`;
    } else if (this.legacyAuthToken) {
      // Append Header
      result.Authorization = `Bearer ${await this.legacyAuthToken}`;
    } else if (this.quickAccessToken) {
      // Append Header
      result.Authorization = `Bearer ${this.quickAccessToken}`;
    }

    // Done
    return result;
  }

  public async hookAppConfig(appConfig: AppConfig): Promise<void> {
    // Set base url
    this.baseUrl = appConfig.backend.url;
  }

  public async hookUser(user: User | undefined): Promise<void> {
    // Log
    if (user) {
      // Debug Token
      this.logger.info('Authorized User added');
    } else {
      // Log
      this.logger.debug('Authorized User removed');
    }

    // Set
    this.user = user;
  }

  public async hookQuickAccess(authToken?: string): Promise<void> {
    // Set
    this.quickAccessToken = authToken;

    // Log
    this.logger.info('Authorized Quick Access updated');
  }

  public async hookLegacyIntegration(authToken: string): Promise<void> {
    this.legacyAuthToken = authToken;
  }

  public getBaseUrl(): string {
    return this.baseUrl ? this.baseUrl : '';
  }

  public async get<T>(path: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return axios.get<T>(path, {
      ...config,
      // https://github.com/axios/axios#request-config
      baseURL: this.baseUrl,
      headers: {
        ...(await this.getHeaders()),
        ...config?.headers,
      },
    });
  }

  public async post<T>(
    path: string,
    data: any,
    config?: BackendApiRequestConfig
  ): Promise<AxiosResponse<T>> {
    return axios.post<T>(path, data, {
      ...config,
      // https://github.com/axios/axios#request-config
      baseURL: this.baseUrl,
      headers: {
        ...(await this.getHeaders()),
        ...config?.headers,
      },
    });
  }

  public async put<T>(
    path: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return axios.put<T>(path, data, {
      ...config,
      // https://github.com/axios/axios#request-config
      baseURL: this.baseUrl,
      headers: {
        ...(await this.getHeaders()),
        ...config?.headers,
      },
    });
  }

  public async patch<T>(
    path: string,
    data?: any,
    config?: BackendApiRequestConfig
  ): Promise<AxiosResponse<T>> {
    return axios.patch<T>(path, data, {
      ...config,
      // https://github.com/axios/axios#request-config
      baseURL: this.baseUrl,
      headers: {
        ...(await this.getHeaders()),
        ...config?.headers,
      },
    });
  }

  public async delete<T>(
    path: string,
    config?: BackendApiRequestConfig
  ): Promise<AxiosResponse<T>> {
    return axios.delete<T>(path, {
      ...config,
      // https://github.com/axios/axios#request-config
      baseURL: this.baseUrl,
      headers: {
        ...(await this.getHeaders()),
        ...config?.headers,
      },
    });
  }
}
